import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  User,
  MapPin,
  Package,
  Calendar,
  ScrollText,
  BookOpen,
  FileText,
  MessageSquare,
  FolderOpen,
  Layers,
  Lightbulb,
  Search,
} from "lucide-react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/client/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/client/components/ui/dialog";
import { collections } from "@/client/db";
import { useSearch, type SearchResult } from "@/client/hooks/use-search";
import { useTabsOptional } from "@/client/hooks/use-tabs-context";
import type { CodexType } from "@/client/hooks/use-codex";

// Icons for codex types
const CODEX_ICONS: Record<CodexType, typeof User> = {
  character: User,
  location: MapPin,
  object: Package,
  event: Calendar,
  lore: ScrollText,
};

// Icons for result types
const TYPE_ICONS: Record<SearchResult["type"], typeof BookOpen> = {
  story: BookOpen,
  codex: User, // Default, will be overridden by codexType
  part: Layers,
  chapter: FolderOpen,
  scene: FileText,
  idea: Lightbulb,
  chat: MessageSquare,
};

interface GlobalSearchProps {
  storyId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearch({
  storyId,
  open,
  onOpenChange,
}: GlobalSearchProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader className="sr-only">
        <DialogTitle>Search</DialogTitle>
        <DialogDescription>
          {storyId
            ? "Search the codex, parts, chapters, scenes, ideas, and chats"
            : "Search your stories"}
        </DialogDescription>
      </DialogHeader>
      <DialogContent className="overflow-hidden p-0" showCloseButton={false}>
        {/* Mounted only while open, so the index isn't built behind a closed dialog */}
        <SearchCommand storyId={storyId} onOpenChange={onOpenChange} />
      </DialogContent>
    </Dialog>
  );
}

function SearchCommand({ storyId, onOpenChange }: Omit<GlobalSearchProps, "open">) {
  const tabs = useTabsOptional();
  const [query, setQuery] = useState("");
  const { search } = useSearch(storyId);
  const navigate = useNavigate();

  // Group results by type
  const groupedResults = useMemo(
    () =>
      search(query).reduce(
        (acc, result) => {
          const group = result.type;
          if (!acc[group]) {
            acc[group] = [];
          }
          acc[group].push(result);
          return acc;
        },
        {} as Record<string, SearchResult[]>
      ),
    [search, query]
  );

  // Handle result selection
  const handleSelect = useCallback(
    (result: SearchResult) => {
      onOpenChange(false);

      if (result.type === "story") {
        // Navigate to the story
        navigate({ to: "/story/$storyId", params: { storyId: result.id } });
      } else if (tabs) {
        // Open in tab using context
        if (result.type === "codex" && result.codexType) {
          tabs.openCodexTab(result.id, result.title, result.codexType);
        } else if (result.type === "scene") {
          tabs.openSceneTab(result.id, result.title);
        } else if (result.type === "chat") {
          tabs.openChatTab(result.id, result.title);
        } else if (result.type === "chapter") {
          tabs.openChapterTab(result.id, result.title);
        } else if (result.type === "part") {
          tabs.openPartTab(result.id, result.title);
        } else if (result.type === "idea" && storyId) {
          // Where it's used, or the board, whose tray holds the ideas not used yet
          const use = Array.from(collections().fragmentLinks.collection.values()).find((link) => link.fragmentId === result.id);
          if (use?.sceneId) tabs.openSceneTab(use.sceneId, "");
          else if (use?.chapterId) tabs.openChapterTab(use.chapterId, "");
          else tabs.openBoardTab(storyId);
        }
      }
    },
    [navigate, onOpenChange, tabs, storyId]
  );

  // Get icon for a result
  const getIcon = (result: SearchResult) => {
    if (result.type === "codex" && result.codexType) {
      return CODEX_ICONS[result.codexType];
    }
    return TYPE_ICONS[result.type];
  };

  // Group labels
  const GROUP_LABELS: Record<SearchResult["type"], string> = {
    story: "Stories",
    codex: "Codex",
    part: "Parts",
    chapter: "Chapters",
    scene: "Scenes",
    idea: "Ideas",
    chat: "Chats",
  };

  // Order of groups
  const GROUP_ORDER: SearchResult["type"][] = [
    "story",
    "codex",
    "part",
    "chapter",
    "scene",
    "idea",
    "chat",
  ];

  return (
    <Command
      shouldFilter={false}
      className="[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
    >
      <CommandInput
        placeholder={
          storyId ? "Search scenes, chapters, ideas, the codex…" : "Search your stories…"
        }
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          <div className="flex flex-col items-center gap-2 py-4">
            <Search className="h-8 w-8 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No results found</p>
          </div>
        </CommandEmpty>

        {GROUP_ORDER.map((type) => {
          const items = groupedResults[type];
          if (!items || items.length === 0) return null;

          return (
            <CommandGroup key={type} heading={GROUP_LABELS[type]}>
              {items.map((result) => {
                const Icon = getIcon(result);
                return (
                  <CommandItem
                    key={`${result.type}-${result.id}`}
                    value={`${result.type}-${result.id}`}
                    onSelect={() => handleSelect(result)}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <div className="flex flex-col gap-0.5 overflow-hidden">
                      <span className="truncate">{result.title}</span>
                      {result.subtitle && (
                        <span className="text-xs text-muted-foreground truncate">
                          {result.subtitle}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          );
        })}
      </CommandList>
    </Command>
  );
}
